import React, { useState, useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Stack,
  Link,
} from '@mui/material';
import {
  DndContext,
  useSensors,
  useSensor,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  closestCenter,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useNavigate } from 'react-router-dom';
import CreateButton from '../../../components/button/CreateButton';
import BasicButton from '../../../components/button/BasicButton';
import EditButton from '../../../components/button/EditButton';
import ActionDialog from '../../../components/dialog/ActionDialog';
import { adminApi } from '../../../api/admin';
import ProgressBar from '../../../components/common/ProgressBar';
import ErrorState from '../../../components/common/ErrorStats';
import NoData from '../../../components/common/NoData';
import SortableBannerRow from './SortableBannerRow';
import theme from '../../../theme';
import useAdminDraftGuard from '../useAdminDraftGuard';

const HEAD_CELLS = [
  { id: 'imageUrl', label: '대표 이미지' },
  { id: 'title', label: '제목' },
  { id: 'url', label: 'URL' },
  { id: 'clickCount', label: '클릭수' },
  { id: 'edit', label: '편집' },
];

const BannerSection = ({
  deviceType,
  banners,
  selectedBannerIds,
  onSelectAll,
  onSelect,
  onEdit,
  onCreate,
  onDelete,
  onOrderUpdate,
  cellStyle,
  textStyle,
  buttonStyle,
}) => {
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [orderedBanners, setOrderedBanners] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const draft = useAdminDraftGuard(
    orderedBanners.map((banner) => banner.id),
    { enabled: isEditingOrder, busy: savingOrder },
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const handleStartEditOrder = () => {
    setOrderedBanners([...banners]);
    setIsEditingOrder(true);
  };

  const handleCancelEditOrder = () => {
    if (!draft.confirmDiscard()) return;
    setIsEditingOrder(false);
    setOrderedBanners([]);
  };

  const handleSaveOrder = async () => {
    if (savingOrder) return;
    setSavingOrder(true);
    const bannerIds = orderedBanners.map((b) => b.id);
    try {
      if (await onOrderUpdate(bannerIds)) setIsEditingOrder(false);
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!isEditingOrder || savingOrder || !over) return;
    if (active.id !== over?.id) {
      setOrderedBanners((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const displayBanners = isEditingOrder ? orderedBanners : banners;

  const numSelectedOnPage = useMemo(
    () => banners.filter((banner) => selectedBannerIds.includes(banner.id)).length,
    [banners, selectedBannerIds],
  );

  const isAllSelected = banners.length > 0 && numSelectedOnPage === banners.length;
  const isIndeterminate = numSelectedOnPage > 0 && !isAllSelected;
  const hasSelection = selectedBannerIds.some((id) => banners.some((b) => b.id === id));

  return (
    <Box>
      {/* 액션 버튼 */}
      <Stack
        component="fieldset"
        disabled={savingOrder}
        direction="row"
        spacing={1}
        sx={{ mb: 2, border: 0, p: 0 }}
      >
        {isEditingOrder ? (
          <>
            <BasicButton variant="outlined" onClick={handleSaveOrder} sx={buttonStyle}>
              저장
            </BasicButton>
            <BasicButton variant="outlined" onClick={handleCancelEditOrder} sx={buttonStyle}>
              취소
            </BasicButton>
          </>
        ) : (
          <>
            <CreateButton text="배너" onClick={() => onCreate(deviceType)} sx={buttonStyle} />
            <BasicButton
              variant="outlined"
              onClick={handleStartEditOrder}
              disabled={banners.length < 2}
              sx={buttonStyle}
            >
              순서 변경
            </BasicButton>
            <BasicButton
              variant="outlined"
              onClick={onDelete}
              disabled={!hasSelection}
              sx={buttonStyle}
            >
              삭제
            </BasicButton>
          </>
        )}
      </Stack>

      {/* 테이블 */}
      <TableContainer component={Paper} sx={{ maxWidth: 1180, boxShadow: 'none' }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey.bg }}>
                <TableCell padding="checkbox" sx={cellStyle}>
                  {isEditingOrder ? (
                    <Box sx={{ width: 42, height: 42 }} />
                  ) : (
                    <Checkbox
                      indeterminate={isIndeterminate}
                      checked={isAllSelected}
                      onChange={(e) => onSelectAll(e, deviceType)}
                      size="small"
                      disabled={banners.length === 0}
                      sx={{
                        color: theme.palette.grey.line,
                        '&.Mui-checked': { color: '#f16438' },
                        '&.MuiCheckbox-indeterminate': { color: '#f16438' },
                      }}
                    />
                  )}
                </TableCell>
                {HEAD_CELLS.filter((cell) => !isEditingOrder || cell.id !== 'edit').map(
                  (headCell, index, arr) => (
                    <TableCell
                      key={headCell.id}
                      align="center"
                      sx={{
                        whiteSpace: 'nowrap',
                        borderRight:
                          index === arr.length - 1
                            ? 'none'
                            : `1px solid ${theme.palette.grey.line}`,
                      }}
                    >
                      <Typography fontSize={14} color={theme.palette.black[4]}>
                        {headCell.label}
                      </Typography>
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isEditingOrder ? (
                <SortableContext
                  items={orderedBanners.map((b) => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {orderedBanners.map((banner) => (
                    <SortableBannerRow
                      key={banner.id}
                      banner={banner}
                      cellStyle={cellStyle}
                      textStyle={textStyle}
                    />
                  ))}
                </SortableContext>
              ) : (
                displayBanners.map((banner) => {
                  const isItemSelected = selectedBannerIds.includes(banner.id);
                  return (
                    <TableRow
                      key={banner.id}
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox" sx={cellStyle}>
                        <Checkbox
                          checked={isItemSelected}
                          onClick={(event) => onSelect(event, banner.id)}
                          size="small"
                          sx={{
                            color: theme.palette.grey.line,
                            '&.Mui-checked': { color: '#f16438' },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          ...cellStyle,
                          minWidth: 306,
                          width: 306,
                          height: 46,
                          p: 0,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          component="img"
                          src={banner.appConfig?.imageUrl || banner.imageUrl}
                          alt={banner.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...cellStyle, minWidth: 300, width: 300 }}>
                        <Typography {...textStyle}>
                          {banner.appConfig?.headline || banner.title}
                        </Typography>
                        {banner.appConfig && (
                          <Typography variant="caption">
                            앱 {banner.appConfig.enabled ? '공개' : '비공개'}
                            {banner.appConfig.appOnly ? ' · 앱 전용' : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellStyle}>
                        <Link
                          href={banner.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textDecoration: 'none' }}
                        >
                          <Typography
                            {...textStyle}
                            sx={{
                              maxWidth: 300,
                              width: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {banner.url}
                          </Typography>
                        </Link>
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        <Typography {...textStyle}>{banner.clickCount.toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ p: 0 }}>
                        <EditButton onToggle={() => onEdit(banner)} />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </DndContext>
      </TableContainer>
      {banners.length === 0 && <NoData message="등록된 배너가 없습니다" />}
    </Box>
  );
};

const BannerManagement = () => {
  const navigate = useNavigate();

  const [pcBanners, setPcBanners] = useState([]);
  const [mobileBanners, setMobileBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedBannerIds, setSelectedBannerIds] = useState([]);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // 다이얼로그 상태
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // 배너 조회 (PC, MOBILE 한번에)
  useEffect(() => {
    const fetchBanners = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminApi.getBanners();
        setPcBanners(response.data.PC || []);
        setMobileBanners(response.data.MOBILE || []);
      } catch (err) {
        setError(err);
        setPcBanners([]);
        setMobileBanners([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBanners();
  }, [refetchTrigger]);

  const handleSelectAllClick = (event, deviceType) => {
    const banners = deviceType === 'PC' ? pcBanners : mobileBanners;
    if (event.target.checked) {
      setSelectedBannerIds((prev) => [...new Set([...prev, ...banners.map((b) => b.id)])]);
      return;
    }
    const bannerIds = banners.map((b) => b.id);
    setSelectedBannerIds((prev) => prev.filter((id) => !bannerIds.includes(id)));
  };

  const handleSelectClick = (event, id) => {
    const selectedIndex = selectedBannerIds.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedBannerIds, id];
    } else {
      newSelected = selectedBannerIds.filter((bannerId) => bannerId !== id);
    }
    setSelectedBannerIds(newSelected);
  };

  const refreshAfterAction = () => {
    setSelectedBannerIds([]);
    setRefetchTrigger((c) => c + 1);
  };

  // 배너 생성 페이지로 이동
  const handleCreateClick = (deviceType) => {
    navigate(`/admin/banners/create?deviceType=${deviceType}`);
  };

  // 배너 삭제
  const handleDelete = async () => {
    try {
      await adminApi.deleteBanners(selectedBannerIds);
      setIsDeleteDialogOpen(false);
      refreshAfterAction();
    } catch (err) {
      // 에러 처리
    }
  };

  // 편집 페이지로 이동
  const handleEditClick = (banner) => {
    navigate(`/admin/banners/${banner.id}/edit?deviceType=${banner.deviceType}`);
  };

  // 순서 저장
  const handleOrderUpdate = async (bannerIds) => {
    try {
      await adminApi.updateBannerOrder(bannerIds);
      refreshAfterAction();
      return true;
    } catch (err) {
      window.alert('배너 순서를 저장하지 못했습니다. 변경한 순서는 유지됩니다. 다시 시도해주세요.');
      return false;
    }
  };

  const cellStyle = { borderRight: `1px solid ${theme.palette.grey.line}` };
  const textStyle = { fontSize: 14, color: theme.palette.black[9] };
  const buttonStyle = { typography: 'body3', fontWeight: 'medium', padding: '12px 16px' };

  return (
    <ProgressBar isLoading={loading}>
      {error ? (
        <ErrorState />
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            앱 홈 상단 롤링 이미지는 Mobile에서 관리합니다. 편집 화면의 앱 이미지·문구·공개·AD
            설정을 사용하세요. PC 및 기존 웹 배너도 그대로 유지됩니다.
          </Alert>
          <Stack spacing={{ xs: '30px', md: '50px', lg: '60px' }}>
            <Box>
              <Typography
                fontSize={{ xs: 18, md: 20, lg: 24 }}
                fontWeight={'bold'}
                color={theme.palette.black[9]}
                sx={{ mb: { xs: '20px', md: '30px' } }}
              >
                PC & Tablet
              </Typography>
              <BannerSection
                deviceType="PC"
                banners={pcBanners}
                selectedBannerIds={selectedBannerIds}
                onSelectAll={handleSelectAllClick}
                onSelect={handleSelectClick}
                onEdit={handleEditClick}
                onCreate={handleCreateClick}
                onDelete={() => setIsDeleteDialogOpen(true)}
                onOrderUpdate={handleOrderUpdate}
                cellStyle={cellStyle}
                textStyle={textStyle}
                buttonStyle={buttonStyle}
              />
            </Box>

            {/* Mobile 배너 */}
            <Box>
              <Typography
                fontSize={{ xs: 18, md: 20, lg: 24 }}
                fontWeight={'bold'}
                color={theme.palette.black[9]}
                sx={{ mb: { xs: '20px', md: '30px' } }}
              >
                Mobile
              </Typography>
              <BannerSection
                deviceType="MOBILE"
                banners={mobileBanners}
                selectedBannerIds={selectedBannerIds}
                onSelectAll={handleSelectAllClick}
                onSelect={handleSelectClick}
                onEdit={handleEditClick}
                onCreate={handleCreateClick}
                onDelete={() => setIsDeleteDialogOpen(true)}
                onOrderUpdate={handleOrderUpdate}
                cellStyle={cellStyle}
                textStyle={textStyle}
                buttonStyle={buttonStyle}
              />
            </Box>
          </Stack>

          {/* 삭제 확인 다이얼로그 */}
          <ActionDialog
            open={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            onConfirm={handleDelete}
            confirmText="삭제"
          >
            <Typography sx={{ textAlign: 'center' }}>
              선택한 {selectedBannerIds.length}개의 배너를 삭제하시겠습니까?
            </Typography>
          </ActionDialog>
        </>
      )}
    </ProgressBar>
  );
};

export default BannerManagement;
